// SPDX-License-Identifier: BUSL-1.1; Copyright (c) 2025 Social Connect Labs, Inc.; Licensed under BUSL-1.1 (see LICENSE); Apache-2.0 from 2029-06-11
import {
  NfcParseError
} from "./chunk-3A3XD7R6.js";
import {
  __require
} from "./chunk-QETQNF76.js";

// src/processing/nfc.ts
var createTextDecoder = () => {
  if (typeof globalThis !== "undefined" && "TextDecoder" in globalThis) {
    return new globalThis.TextDecoder("utf-8", { fatal: true });
  }
  if (typeof globalThis.global !== "undefined" && globalThis.global && "TextDecoder" in globalThis.global) {
    return new globalThis.global.TextDecoder("utf-8", { fatal: true });
  }
  if (typeof globalThis.process !== "undefined" && globalThis.process?.versions?.node) {
    try {
      const req = typeof __require === "function" ? __require : void 0;
      const util = req ? req("node:util") : void 0;
      if (util?.TextDecoder) {
        return new util.TextDecoder("utf-8", { fatal: true });
      }
    } catch {
    }
  }
  throw new NfcParseError(
    "TextDecoder not available in this environment. This SDK requires TextDecoder support which is available in modern browsers, Node.js, and React Native."
  );
};
var DECODER;
var getDecoder = () => {
  if (!DECODER) DECODER = createTextDecoder();
  return DECODER;
};
var TAG_DG1 = 97;
var TAG_DG2 = 117;
function readLength(view, offset) {
  if (offset >= view.length) {
    throw new NfcParseError("Unexpected end of data while reading length");
  }
  const first = view[offset];
  if (first & 128) {
    const bytes = first & 127;
    if (bytes === 0) {
      throw new NfcParseError("Indefinite length (0x80) not supported");
    }
    if (offset + bytes >= view.length) {
      throw new NfcParseError("Unexpected end of data while reading long-form length");
    }
    let len = 0;
    for (let j = 1; j <= bytes; j++) {
      len = len << 8 | view[offset + j];
    }
    return { length: len, next: offset + 1 + bytes };
  }
  return { length: first, next: offset + 1 };
}
function parseNFCResponse(bytes) {
  const result = {};
  let i = 0;
  while (i < bytes.length) {
    const tag = bytes[i++];
    if (i >= bytes.length) throw new NfcParseError("Unexpected end of data");
    const { length, next } = readLength(bytes, i);
    i = next;
    if (i + length > bytes.length) throw new NfcParseError("Unexpected end of data");
    const value = bytes.slice(i, i + length);
    i += length;
    switch (tag) {
      case TAG_DG1: {
        result.dg1 = { mrz: getDecoder().decode(value) };
        break;
      }
      case TAG_DG2: {
        result.dg2 = { image: value };
        break;
      }
      default: {
        break;
      }
    }
  }
  return result;
}

// src/nfc/normalizePassport.ts
var BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
function base64ToBytes(input) {
  const eqIndex = input.indexOf("=");
  const body = eqIndex === -1 ? input : input.slice(0, eqIndex);
  const padding = eqIndex === -1 ? "" : input.slice(eqIndex);
  if (!/^[A-Za-z0-9+/]*$/.test(body) || !/^={0,2}$/.test(padding)) {
    throw new Error("NFC normalization failed: invalid base64 (illegal characters)");
  }
  const clean = body;
  const lookup = new Int16Array(256).fill(-1);
  for (let i2 = 0; i2 < BASE64_ALPHABET.length; i2++) {
    lookup[BASE64_ALPHABET.charCodeAt(i2)] = i2;
  }
  const fullGroups = Math.floor(clean.length / 4);
  const remainder = clean.length % 4;
  if (remainder === 1) {
    throw new Error("NFC normalization failed: invalid base64 length (truncated group)");
  }
  const outLen = fullGroups * 3 + (remainder === 0 ? 0 : remainder - 1);
  const out = new Uint8Array(outLen);
  let o = 0;
  let i = 0;
  for (; i + 4 <= clean.length; i += 4) {
    const a = lookup[clean.charCodeAt(i)];
    const b = lookup[clean.charCodeAt(i + 1)];
    const c = lookup[clean.charCodeAt(i + 2)];
    const d = lookup[clean.charCodeAt(i + 3)];
    out[o++] = a << 2 | b >> 4;
    out[o++] = (b & 15) << 4 | c >> 2;
    out[o++] = (c & 3) << 6 | d;
  }
  if (remainder === 2) {
    const a = lookup[clean.charCodeAt(i)];
    const b = lookup[clean.charCodeAt(i + 1)];
    out[o++] = a << 2 | b >> 4;
  } else if (remainder === 3) {
    const a = lookup[clean.charCodeAt(i)];
    const b = lookup[clean.charCodeAt(i + 1)];
    const c = lookup[clean.charCodeAt(i + 2)];
    out[o++] = a << 2 | b >> 4;
    out[o++] = (b & 15) << 4 | c >> 2;
  }
  return out;
}
function base64ToSignedBytes(input) {
  const bytes = base64ToBytes(input);
  const out = new Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    out[i] = b > 127 ? b - 256 : b;
  }
  return out;
}
function decodeRequiredSignedBytes(input, field) {
  const bytes = base64ToSignedBytes(input);
  if (bytes.length === 0) {
    throw new Error(`NFC normalization failed: ${field} present but empty/malformed`);
  }
  return bytes;
}
function hexToUnsignedBytes(hex) {
  if (!/^[0-9a-fA-F]*$/.test(hex)) {
    throw new Error("NFC normalization failed: invalid hex (illegal characters)");
  }
  if (hex.length % 2 !== 0) {
    throw new Error("NFC normalization failed: invalid hex length (odd)");
  }
  const out = [];
  for (let i = 0; i + 1 < hex.length; i += 2) {
    out.push(parseInt(hex.slice(i, i + 2), 16));
  }
  return out;
}
function asObject(raw) {
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
    }
  }
  if (raw && typeof raw === "object") return raw;
  throw new Error("NFC normalization failed: reader result is not an object");
}
function pickString(src, keys) {
  for (const key of keys) {
    const value = src[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return void 0;
}
function normalizeDsc(raw) {
  let s = raw.trim();
  if (s.startsWith("{")) {
    try {
      const parsed = JSON.parse(s);
      if (typeof parsed?.PEM === "string") s = parsed.PEM.trim();
    } catch {
    }
  }
  const hasBegin = s.includes("BEGIN CERTIFICATE");
  const hasEnd = s.includes("END CERTIFICATE");
  if (hasBegin !== hasEnd) {
    throw new Error("NFC normalization failed: dsc has mismatched BEGIN/END certificate markers");
  }
  if (hasBegin) return s;
  const body = s.replace(/\s+/g, "");
  return `-----BEGIN CERTIFICATE-----
${body}
-----END CERTIFICATE-----`;
}
function dgHashesObject(raw) {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return void 0;
    }
  }
  if (value && typeof value === "object") return value;
  return void 0;
}
function dgHashHex(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") {
    const sodHash = entry.sodHash;
    if (typeof sodHash === "string") return sodHash;
  }
  return void 0;
}
function dgKeyToNumber(key) {
  const match = key.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
}
function normalizeNfcPassport(raw) {
  const src = asObject(raw);
  const mrzRaw = pickString(src, ["mrzString", "mrz", "passportMRZ"]);
  if (!mrzRaw) {
    throw new Error("NFC normalization failed: missing MRZ (looked for mrzString/mrz/passportMRZ)");
  }
  const mrz = mrzRaw.replace(/[\r\n]/g, "");
  const signedAttrB64 = pickString(src, ["signedAttributes", "eContent"]);
  const eContentB64 = pickString(src, ["encapContent", "eContentBase64"]);
  const encryptedDigestB64 = pickString(src, ["encryptedDigest", "signature", "signatureBase64"]);
  const dscRaw = pickString(src, ["documentSigningCertificate", "dsc"]);
  const missing = [];
  if (!signedAttrB64) missing.push("signedAttr (signedAttributes|eContent)");
  if (!eContentB64) missing.push("eContent (encapContent|eContentBase64)");
  if (!encryptedDigestB64) missing.push("encryptedDigest (encryptedDigest|signature|signatureBase64)");
  if (!dscRaw) missing.push("dsc (documentSigningCertificate|dsc)");
  if (missing.length > 0) {
    throw new Error(`NFC normalization failed: missing required field(s): ${missing.join(", ")}`);
  }
  const eContent = decodeRequiredSignedBytes(eContentB64, "eContent");
  const signedAttr = decodeRequiredSignedBytes(signedAttrB64, "signedAttr");
  const encryptedDigest = decodeRequiredSignedBytes(encryptedDigestB64, "encryptedDigest");
  const dsc = normalizeDsc(dscRaw);
  if (dsc.replace(/-----(BEGIN|END) CERTIFICATE-----/g, "").replace(/\s+/g, "").length === 0) {
    throw new Error("NFC normalization failed: dsc present but empty/malformed");
  }
  const documentType = mrz.length === 88 ? "passport" : "id_card";
  const passportData = {
    mrz,
    eContent,
    signedAttr,
    encryptedDigest,
    dsc,
    documentType,
    documentCategory: documentType,
    mock: false
  };
  const dgHashes = dgHashesObject(src["dataGroupHashes"]);
  if (dgHashes) {
    const dg1Hex = dgHashHex(dgHashes["1"] ?? dgHashes["DG1"]);
    const dg2Hex = dgHashHex(dgHashes["2"] ?? dgHashes["DG2"]);
    if (dg1Hex) passportData.dg1Hash = hexToUnsignedBytes(dg1Hex);
    if (dg2Hex) passportData.dg2Hash = hexToUnsignedBytes(dg2Hex);
    const present = Object.keys(dgHashes).map(dgKeyToNumber).filter((n) => n !== null).sort((a, b) => a - b);
    if (present.length > 0) passportData.dgPresents = present;
  }
  return passportData;
}

// src/nfc/index.ts
async function scanNFC(selfClient, opts) {
  const baseContext = {
    sessionId: opts.sessionId,
    userId: opts.userId,
    platform: selfClient?.config?.platform ?? "unknown",
    scanType: opts.useCan ? "can" : "mrz"
  };
  selfClient.emit("NFC_EVENT" /* NFC_EVENT */, {
    level: "info",
    context: {
      ...baseContext,
      stage: "start"
    },
    event: "scan_start"
  });
  try {
    return await selfClient.scanNFC(opts);
  } catch (error) {
    selfClient.emit("NFC_EVENT" /* NFC_EVENT */, {
      level: "error",
      context: {
        ...baseContext,
        stage: "scan"
      },
      event: "scan_failed",
      details: {
        error: error instanceof Error ? error.message : String(error)
      }
    });
    throw error;
  }
}

export {
  parseNFCResponse,
  normalizeNfcPassport,
  scanNFC
};
//# sourceMappingURL=chunk-7QVT2WO5.js.map